import React, { useState, useEffect } from 'react';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import type { Tables } from '../lib/types';
import { ITALIAN_PROVINCES } from '../constants/cities';
import { supabase } from '../lib/supabase';

interface NewLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (locationData: any) => void;
  initialData?: Tables<'locations'>;
}

const NewLocationModal: React.FC<NewLocationModalProps> = ({ isOpen, onClose, onSubmit, initialData }) => {
  const [locationData, setLocationData] = useState({
    name: '',
    address: '',
    city: '',
    capacity: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    imageUrl: ''
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [imagePosition, setImagePosition] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (initialData) {
      setLocationData({
        name: initialData.name || '',
        address: initialData.address || '',
        city: initialData.city || '',
        capacity: initialData.capacity?.toString() || '',
        contactName: initialData.contact_name || '',
        contactPhone: initialData.contact_phone || '',
        contactEmail: initialData.contact_email || '',
        imageUrl: initialData.image_url || ''
      });
      setImagePreview(initialData.image_url || null);
      setSelectedImage(null);
      setImagePosition({
        x: initialData.image_position_x ?? 50,
        y: initialData.image_position_y ?? 50
      });
    } else {
      setLocationData({
        name: '',
        address: '',
        city: '',
        capacity: '',
        contactName: '',
        contactPhone: '',
        contactEmail: '',
        imageUrl: ''
      });
      setImagePreview(null);
      setSelectedImage(null);
      setImagePosition({ x: 50, y: 50 });
    }
  }, [initialData, isOpen]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
        setImagePosition({ x: 50, y: 50 });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;

    const deltaX = (e.clientX - dragStart.x) / 2;
    const deltaY = (e.clientY - dragStart.y) / 2;

    setImagePosition(prev => ({
      x: Math.max(0, Math.min(100, prev.x + deltaX)),
      y: Math.max(0, Math.min(100, prev.y + deltaY))
    }));

    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setLocationData({ ...locationData, imageUrl: '' });
    setImagePosition({ x: 50, y: 50 });
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!selectedImage) return locationData.imageUrl || null;

    setUploading(true);
    try {
      const fileExt = selectedImage.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${fileName}`;

      console.log('Uploading file:', { name: selectedImage.name, size: selectedImage.size, type: selectedImage.type, filePath });

      const { error: uploadError, data: uploadData } = await supabase.storage
        .from('location-images')
        .upload(filePath, selectedImage, {
          cacheControl: '3600',
          upsert: false
        });

      console.log('Upload result:', { uploadError, uploadData });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('location-images')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const imageUrl = await uploadImage();

      const formattedData = {
        ...locationData,
        name: locationData.name.toUpperCase(),
        imageUrl: imageUrl || locationData.imageUrl,
        imagePositionX: imagePosition.x,
        imagePositionY: imagePosition.y
      };

      onSubmit(formattedData);
      onClose();
    } catch (error: any) {
      console.error('Error submitting form:', error);
      alert(`Errore durante il caricamento dell'immagine: ${error?.message || error?.statusCode || JSON.stringify(error)}`);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocationData({
      ...locationData,
      name: e.target.value.toUpperCase()
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            {initialData ? 'Modifica Location' : 'Nuova Location'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nome Location</label>
              <input
                type="text"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500 uppercase"
                value={locationData.name}
                onChange={handleNameChange}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Indirizzo</label>
              <input
                type="text"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                value={locationData.address}
                onChange={(e) => setLocationData({ ...locationData, address: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Città</label>
              <select
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                value={locationData.city}
                onChange={(e) => setLocationData({ ...locationData, city: e.target.value })}
              >
                <option value="">Seleziona una città</option>
                {ITALIAN_PROVINCES.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Capacità</label>
              <input
                type="number"
                required
                min="2"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                value={locationData.capacity}
                onChange={(e) => setLocationData({ ...locationData, capacity: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Immagine Location</label>
              {imagePreview ? (
                <div className="space-y-2">
                  <div
                    className="relative overflow-hidden rounded-lg border border-gray-300 h-48 cursor-move select-none"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  >
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      style={{
                        objectPosition: `${imagePosition.x}% ${imagePosition.y}%`
                      }}
                      draggable={false}
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 z-10"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 text-center">
                    Trascina l'immagine per riposizionarla
                  </p>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-rose-500 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <ImageIcon className="h-12 w-12 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">Clicca per caricare un'immagine</span>
                    <span className="text-xs text-gray-500 mt-1">PNG, JPG, GIF fino a 10MB</span>
                  </label>
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Informazioni di Contatto</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nome Referente</label>
                  <input
                    type="text"
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                    value={locationData.contactName}
                    onChange={(e) => setLocationData({ ...locationData, contactName: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Telefono Referente</label>
                  <input
                    type="tel"
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                    value={locationData.contactPhone}
                    onChange={(e) => setLocationData({ ...locationData, contactPhone: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email Referente</label>
                  <input
                    type="email"
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                    value={locationData.contactEmail}
                    onChange={(e) => setLocationData({ ...locationData, contactEmail: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={uploading}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="px-4 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700 disabled:opacity-50 flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <Upload className="h-4 w-4 animate-pulse" />
                  Caricamento...
                </>
              ) : (
                initialData ? 'Salva Modifiche' : 'Aggiungi Location'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewLocationModal;